/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  CLIENT_SESSION_TOKEN_KEY,
  getClientSessionToken,
} from "../../api/client";
import {
  clearShareLinkToken,
  getMeTokenBinding,
  type MeTokenBinding,
} from "./meToken";

/**
 * /me client-session auth.
 *
 * Reuses the magic-link → JWT MECHANISM (`/web/auth/verify` exchanges the
 * magic-link token for a 365-day JWT carrying `person_id`), but stores the JWT
 * under the `/me` `client_session_token` key (NOT the legacy
 * `guided_chat_token`). The orval `customInstance` injects it only for
 * `/me/*` API requests.
 *
 * This context owns AUTH ONLY (does the client have a session token). The
 * acting-partner identity is NOT derived here: the JWT's `person_id` claim is
 * the DB PK, whereas partners are keyed by `person_global_uuid`, so decoding the
 * token cannot resolve "which partner am I". `MeShell` instead reads the
 * server-authoritative `acting_partner_id` off the `/me/couple` payload.
 */

interface MeAuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  /**
   * Which token is held: the couple-scoped pre-bind token (`"couple"`), the
   * person-bound post-bind token (`"person"`), or none (`null`). `MeShell`
   * branches its couple-source on this so the pre-bind picker reads the
   * least-privilege roster and never hits the person-only couple read.
   */
  binding: MeTokenBinding;
  setToken: (token: string | null) => void;
  logout: () => void;
}

const MeAuthContext = createContext<MeAuthContextValue | null>(null);

interface MeAuthProviderProps {
  children: ReactNode;
  /**
   * Fires when the held token VALUE changes (setToken/logout to a different
   * value; a same-value re-set is a no-op), with the PREVIOUS token so the
   * handler can tell an identity SWITCH from the pre-bind→post-bind UPGRADE.
   * The /me query keys are exact per contracts §4.4 (no token suffix), so
   * MeApp registers its QueryClient cache-clear here — an in-tab identity
   * switch (`/me/dev/login`, logout→login) must never serve the previous
   * identity's cached reads. The upgrade must NOT clear: see MeApp's handler.
   */
  onTokenChange?: (token: string | null, previousToken: string | null) => void;
}

export function MeAuthProvider({ children, onTokenChange }: MeAuthProviderProps) {
  const [token, setTokenState] = useState<string | null>(() =>
    getClientSessionToken()
  );
  // Refs keep `setToken` stable (empty deps) while still comparing against
  // the CURRENT token and calling the CURRENT callback.
  const tokenRef = useRef<string | null>(token);
  const onTokenChangeRef = useRef(onTokenChange);
  onTokenChangeRef.current = onTokenChange;

  const setToken = useCallback((next: string | null) => {
    const previous = tokenRef.current;
    const changed = previous !== next;
    tokenRef.current = next;
    setTokenState(next);
    if (next) {
      localStorage.setItem(CLIENT_SESSION_TOKEN_KEY, next);
    } else {
      localStorage.removeItem(CLIENT_SESSION_TOKEN_KEY);
      // A full logout also drops the stashed shared-link token — the next
      // session re-stashes it from a fresh `/me/auth?token=` landing.
      clearShareLinkToken();
    }
    if (changed) {
      onTokenChangeRef.current?.(next, previous);
    }
  }, []);

  const value = useMemo<MeAuthContextValue>(
    () => ({
      token,
      isAuthenticated: !!token,
      binding: getMeTokenBinding(token),
      setToken,
      logout: () => setToken(null),
    }),
    [token, setToken]
  );

  return (
    <MeAuthContext.Provider value={value}>{children}</MeAuthContext.Provider>
  );
}

export function useMeAuth(): MeAuthContextValue {
  // INVARIANT: the provider-missing throw must stay AFTER the single
  // useContext call, with NO hooks added after it. useMeFeatureFlags wraps
  // this call in try/catch for provider-less renders — that idiom is
  // hook-order-stable only while this function's hook sequence is exactly
  // [useContext] on both the throwing and non-throwing paths.
  const ctx = useContext(MeAuthContext);
  if (!ctx) {
    throw new Error("useMeAuth must be used within MeAuthProvider");
  }
  return ctx;
}
